/* Limitations page — consolidates ALL clinical & technical limitations
 * for radical transparency. Linked from Footer + Trust Badge modal.
 *
 * Soft launch readiness: this is the page a regulator, partner doctor,
 * or skeptical patient lands on when they want to know "what does
 * Nutramilo NOT claim to do?"
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";
import {
  AlertTriangle, ShieldCheck, BookOpen, Stethoscope, Activity,
  XCircle, CheckCircle2, ArrowLeft, FlaskConical,
} from "lucide-react";

const TX = {
  bg: {
    title: "Прозрачност на ограниченията",
    intro:
      "Nutramilo е wellness и образователна платформа. Тази страница списва ясно какво НЕ можем — за да вземате информирани решения за здравето си.",
    can_title: "Какво Nutramilo МОЖЕ",
    can_items: [
      "Изчислява клинични индекси (HOMA-IR, eGFR, ACR) от вашите лабораторни — стандартни, peer-reviewed формули",
      "Генерира DIL/glycemic load анализ за храна — собствен модел, валидиран с unit тестове",
      "Стратифицира дентален риск (CAMBRA + EFP 2018) — академични протоколи",
      "Предлага наблюдения от рентгенови снимки — само observational, не диагностични",
      "Свързва множество модули в обединен метаболитен фенотип чрез weighted consensus",
      "Препраща към квалифицирани специалисти, когато се идентифицират концерни",
    ],
    cant_title: "Какво Nutramilo НЕ Е и НЕ МОЖЕ",
    cant_items: [
      {
        title: "Не е медицинско изделие (CE Class IIa)",
        body: "Не сме сертифицирани под EU MDR 2017/745. Не диагностицираме, не предписваме лекарствено лечение, не предсказваме изход на заболяване.",
      },
      {
        title: "Не заместваме лекар",
        body: "Всеки наш сигнал — от композитен фенотип до X-ray observations — е насочен към обсъждане с лицензиран медицински специалист, не към автономно решение.",
      },
      {
        title: "AI Coach препоръките не са клинично валидирани",
        body: "Текстовите отговори на нашия AI Coach и NutriBot са constraint-нати чрез системни prompts, но не са независимо ground-truth-нати срещу експертни мнения. Препоръчваме винаги cross-check с лекар.",
      },
      {
        title: "Не сме провеждали проспективно проучване",
        body: "Не разполагаме с RCT данни за ефективността на нашите интервенции. Подобряване на HOMA-IR/HbA1c след следване на нашите препоръки не е независимо доказано.",
      },
      {
        title: "Калибровката е международна, не BG-специфична",
        body: "Cutoffs за лабораторни (KDIGO, ADA, EFP) са международни. Локализирана нормализация за български кохорт е в roadmap-а.",
      },
      {
        title: "X-ray и рентгенови снимки — само observational",
        body: "Vision модулите използват GPT-5.2 под стриктни observational prompts. Не откриваме кариес, пародонтит, тумори или други клинични находки. Винаги обсъждайте резултатите със зъболекар.",
      },
    ],
    validation_title: "Какво е валидирано",
    validation_body:
      "Клинични формули са независимо тествани срещу peer-reviewed публикации (Matthews 1985, Inker 2021, Featherstone 2007, Tonetti 2018, Holt 1997, Salmerón 1997, Lovallo 2005-2006). Цялата методология е публично достъпна.",
    validation_cta: "Прочетете методологията",
    risk_title: "Кога НЕ трябва да използвате Nutramilo",
    risk_items: [
      "Имате тежки симптоми, изискващи спешна медицинска помощ — 112 / болница",
      "Получавате текущо лечение и трябва промени в режима — само с вашия лекар",
      "Сте бременна или планирате — само под лекарско наблюдение",
      "Имате диагноза, изискваща professional dietitian (хемодиализа, рядко метаболитно заболяване и т.н.)",
    ],
    rights_title: "Вашите права",
    rights_body:
      "Имате пълно право на достъп, експорт и изтриване на всички данни, които съхраняваме за вас. Свържете се с настройките на профила си или ни пишете на support@nutramilo.com.",
    last_updated: "Последно обновено: април 2026",
    back: "Начало",
  },
  en: {
    title: "Transparency of Limitations",
    intro:
      "Nutramilo is a wellness and educational platform. This page lists clearly what we CANNOT do — so you can make informed decisions about your health.",
    can_title: "What Nutramilo CAN",
    can_items: [
      "Compute clinical indices (HOMA-IR, eGFR, ACR) from your labs — standard, peer-reviewed formulas",
      "Generate DIL / glycemic load analysis for foods — proprietary model, validated with unit tests",
      "Stratify dental risk (CAMBRA + EFP 2018) — academic protocols",
      "Surface observations from radiographs — observational only, never diagnostic",
      "Combine multiple modules into a unified metabolic phenotype via weighted consensus",
      "Refer you to qualified specialists when concerns are identified",
    ],
    cant_title: "What Nutramilo IS NOT and CANNOT do",
    cant_items: [
      {
        title: "Not a medical device (CE Class IIa)",
        body: "We are not certified under EU MDR 2017/745. We do not diagnose, prescribe medication, or predict disease outcomes.",
      },
      {
        title: "Not a replacement for a doctor",
        body: "Every signal we surface — from composite phenotype to X-ray observations — is meant to inform a conversation with a licensed medical professional, not autonomous decision-making.",
      },
      {
        title: "AI Coach suggestions are not clinically validated",
        body: "Text outputs of our AI Coach and NutriBot are constrained by system prompts but not independently ground-truthed against expert opinion. Always cross-check with a doctor.",
      },
      {
        title: "No prospective RCT data",
        body: "We have not conducted a prospective randomised controlled trial. Improvements in HOMA-IR/HbA1c after following our recommendations are not independently proven.",
      },
      {
        title: "International cutoffs, not BG-specific",
        body: "Lab cutoffs (KDIGO, ADA, EFP) are international. Local normalisation for the Bulgarian cohort is on our roadmap.",
      },
      {
        title: "X-ray and radiographs — observational only",
        body: "Vision modules use GPT-5.2 under strict observational prompts. We do not detect caries, periodontitis, tumours or other clinical findings. Always discuss with your dentist.",
      },
    ],
    validation_title: "What IS validated",
    validation_body:
      "Clinical formulas are independently tested against peer-reviewed publications (Matthews 1985, Inker 2021, Featherstone 2007, Tonetti 2018, Holt 1997, Salmerón 1997, Lovallo 2005-2006). The full methodology is publicly available.",
    validation_cta: "Read the methodology",
    risk_title: "When you should NOT use Nutramilo",
    risk_items: [
      "You have severe symptoms requiring emergency care — 112 / hospital",
      "You're under active treatment and need regimen changes — only with your doctor",
      "You are pregnant or planning to be — only under medical supervision",
      "You have a condition requiring a registered dietitian (haemodialysis, rare metabolic disease, etc.)",
    ],
    rights_title: "Your rights",
    rights_body:
      "You have full rights of access, export and deletion of every piece of data we hold about you. Visit your profile settings or email support@nutramilo.com.",
    last_updated: "Last updated: April 2026",
    back: "Home",
  },
  es: {
    title: "Transparencia sobre las Limitaciones",
    intro:
      "Nutramilo es una plataforma de bienestar y educación. Esta página enumera claramente lo que NO podemos hacer — para que puedas tomar decisiones informadas sobre tu salud.",
    can_title: "Qué PUEDE hacer Nutramilo",
    can_items: [
      "Calcular índices clínicos (HOMA-IR, eGFR, ACR) a partir de tus análisis — fórmulas estándar revisadas por pares",
      "Generar análisis DIL / carga glucémica para alimentos — modelo propietario validado con pruebas unitarias",
      "Estratificar el riesgo dental (CAMBRA + EFP 2018) — protocolos académicos",
      "Presentar observaciones a partir de radiografías — únicamente observacional, nunca diagnóstico",
      "Combinar múltiples módulos en un fenotipo metabólico unificado mediante consenso ponderado",
      "Derivarte a especialistas cualificados cuando se identifican preocupaciones",
    ],
    cant_title: "Qué NO ES Nutramilo y qué NO PUEDE hacer",
    cant_items: [
      {
        title: "No es un dispositivo médico (Clase IIa CE)",
        body: "No estamos certificados bajo el Reglamento UE MDR 2017/745. No diagnosticamos, prescribimos medicamentos ni predecimos resultados clínicos.",
      },
      {
        title: "No sustituye a un médico",
        body: "Cada señal que presentamos — desde el fenotipo compuesto hasta las observaciones de radiografías — está destinada a informar una conversación con un profesional médico autorizado, no a la toma de decisiones autónoma.",
      },
      {
        title: "Las sugerencias del AI Coach no están validadas clínicamente",
        body: "Los textos generados por nuestro AI Coach y NutriBot están limitados por instrucciones del sistema, pero no han sido contrastados de forma independiente con la opinión de expertos. Consulta siempre con un médico.",
      },
      {
        title: "Sin datos de ECA prospectivo",
        body: "No hemos realizado un ensayo controlado aleatorizado prospectivo. Las mejoras en HOMA-IR/HbA1c tras seguir nuestras recomendaciones no están demostradas de forma independiente.",
      },
      {
        title: "Puntos de corte internacionales, no específicos para Bulgaria",
        body: "Los puntos de corte de laboratorio (KDIGO, ADA, EFP) son internacionales. La normalización local para la cohorte búlgara está en nuestra hoja de ruta.",
      },
      {
        title: "Radiografías — únicamente observacional",
        body: "Los módulos de visión utilizan GPT-5.2 bajo instrucciones estrictamente observacionales. No detectamos caries, periodontitis, tumores ni otros hallazgos clínicos. Consulta siempre con tu dentista.",
      },
    ],
    validation_title: "Qué SÍ está validado",
    validation_body:
      "Las fórmulas clínicas se prueban de forma independiente frente a publicaciones revisadas por pares (Matthews 1985, Inker 2021, Featherstone 2007, Tonetti 2018, Holt 1997, Salmerón 1997, Lovallo 2005-2006). La metodología completa está disponible públicamente.",
    validation_cta: "Leer la metodología",
    risk_title: "Cuándo NO deberías usar Nutramilo",
    risk_items: [
      "Tienes síntomas graves que requieren atención de urgencias — 112 / hospital",
      "Estás en tratamiento activo y necesitas cambios en el régimen — solo con tu médico",
      "Estás embarazada o planeas estarlo — solo bajo supervisión médica",
      "Tienes una afección que requiere un dietista registrado (hemodiálisis, enfermedad metabólica rara, etc.)",
    ],
    rights_title: "Tus derechos",
    rights_body:
      "Tienes plenos derechos de acceso, exportación y eliminación de todos los datos que conservamos sobre ti. Visita la configuración de tu perfil o escribe a support@nutramilo.com.",
    last_updated: "Última actualización: abril de 2026",
    back: "Inicio",
  },
};

export default function LimitationsPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const t = TX[bg ? "bg" : "en"];
  // Live validation counter — falls back gracefully if endpoint is down
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/validation/public-status`)
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
  }, []);
  const passingText = stats?.ok && stats?.passed
    ? `${stats.passed}/${stats.total} ${bg ? "теста passing" : "tests passing"}`
    : (bg ? "всички тестове passing" : "all tests passing");

  const cardCls = isDark
    ? "bg-[hsl(223,47%,11%)] border border-white/5"
    : "bg-white border border-gray-100 shadow-sm";

  return (
    <div className={isDark ? "min-h-screen bg-[hsl(223,47%,7%)] text-gray-100" : "min-h-screen bg-[#F8FAFC] text-gray-900"}>
      <SEO title={t.title} description={t.intro} path="/legal/limitations" />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100 mb-6">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>

        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3">
          {t.title}
        </h1>
        <p className={`text-base mb-8 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {t.intro}
        </p>

        {/* CAN do */}
        <section className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-5 border-l-4 border-emerald-400`}>
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3 text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-5 h-5" /> {t.can_title}
          </h2>
          <ul className="space-y-2 text-sm">
            {t.can_items.map((it, i) => (
              <li key={`can-${i}-${String(it).slice(0, 30)}`} className="flex items-start gap-2">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CANNOT do */}
        <section className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-5 border-l-4 border-red-500`}>
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-4 text-red-600 dark:text-red-400">
            <XCircle className="w-5 h-5" /> {t.cant_title}
          </h2>
          <ul className="space-y-3">
            {t.cant_items.map((it, i) => (
              <li key={`cant-${it.title}`} className={`p-3 rounded-xl ${isDark ? "bg-white/5" : "bg-[#F8FAFC]"}`}>
                <div className="font-semibold text-sm mb-1">{it.title}</div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>{it.body}</div>
              </li>
            ))}
          </ul>
        </section>

        {/* Validation */}
        <section className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-5 border-l-4 border-[#0EA5E9]`}>
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3 text-[#0EA5E9]">
            <FlaskConical className="w-5 h-5" /> {t.validation_title}
          </h2>
          <div className={`text-xs font-semibold mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
            ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}
            data-testid="limitations-validation-counter">
            <CheckCircle2 className="w-3.5 h-3.5" /> {passingText}
          </div>
          <p className={`text-sm mb-4 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {t.validation_body}
          </p>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/validation-methodology`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0EA5E9]/15 hover:bg-[#0EA5E9]/25 text-[#0EA5E9] text-sm font-medium mr-2"
          >
            <BookOpen className="w-4 h-4" /> {t.validation_cta}
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/public/validation-notebook.ipynb`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="limitations-colab-notebook"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-sm font-medium"
          >
            <FlaskConical className="w-4 h-4" />
            {bg ? "Свалете Colab notebook за одит" : "Download Colab audit notebook"}
          </a>
        </section>

        {/* When NOT to use */}
        <section className="mb-5 p-5 rounded-2xl border-2 border-amber-400 bg-amber-50/30 dark:bg-amber-900/10">
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5" /> {t.risk_title}
          </h2>
          <ul className="space-y-2 text-sm">
            {t.risk_items.map((it, i) => (
              <li key={`risk-${i}-${String(it).slice(0, 30)}`} className="flex items-start gap-2">
                <Stethoscope className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                <span className={isDark ? "text-amber-200/80" : "text-amber-900/80"}>{it}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Rights */}
        <section className={`${cardCls} rounded-2xl p-5 sm:p-6 mb-8 border-l-4 border-[#6366F1]`}>
          <h2 className="flex items-center gap-2 text-lg font-semibold mb-3">
            <ShieldCheck className="w-5 h-5 text-[#6366F1]" /> {t.rights_title}
          </h2>
          <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {t.rights_body}
          </p>
        </section>

        <p className="text-xs opacity-50 text-center mb-8">{t.last_updated}</p>
      </div>
      <AppFooter />
    </div>
  );
}
